import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { KPI_CONFIG } from '@/types/kpi';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface KpiDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  kpiType: string | null;
  sellerId: string;
  currentValue: number;
}

export const KpiDetailModal: React.FC<KpiDetailModalProps> = ({
  isOpen,
  onClose,
  kpiType,
  sellerId,
  currentValue
}) => {
  const [historicalData, setHistoricalData] = useState<Array<{ month: string; value: number }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [pausedCustomers, setPausedCustomers] = useState<Array<{ 
    contract_id: string;
    first_name: string;
    last_name: string;
    agency_name: string;
    days_on_pause: number;
    last_departure: string;
    next_arrival: string | null;
    next_stage: string | null;
  }>>([]);
  
  const [activeCustomers, setActiveCustomers] = useState<Array<{
    cs_id: string;
    first_name: string;
    last_name: string;
    agency_name: string;
    bill_start: string;
    bill_end: string;
    duration_days: number;
  }>>([]);

  // Generate months from January 2024 to current month
  const generateMonths = () => {
    const months = [];
    const startDate = new Date(2024, 0, 1); // January 2024
    const currentDate = new Date();
    
    while (startDate <= currentDate) {
      months.push({
        start: new Date(startDate).toISOString().split('T')[0],
        end: new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).toISOString().split('T')[0],
        label: startDate.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })
      });
      startDate.setMonth(startDate.getMonth() + 1);
    }
    
    return months;
  };

  useEffect(() => {
    if (isOpen && kpiType) {
      fetchHistoricalData();
    }
  }, [isOpen, kpiType, sellerId]);

  const fetchHistoricalData = async () => {
    setIsLoading(true);
    setLoadingProgress(0);
    const months = generateMonths();
    const results = [];

    try {
      // Fetch data sequentially with delay to avoid rate limiting
      for (let i = 0; i < months.length; i++) {
        const month = months[i];
        setLoadingProgress(Math.round((i / months.length) * 100));
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/kpi/${sellerId}?from=${month.start}&to=${month.end}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            // Extract only the specific KPI value we need
            results.push({
              month: month.label,
              value: data[kpiType!] || 0
            });
          } else if (response.status === 404) {
            // No data for this month - that's okay
            results.push({ month: month.label, value: 0 });
          } else if (response.status === 429) {
            // Rate limited - wait a bit longer
            await new Promise(resolve => setTimeout(resolve, 1000));
            // Retry once
            const retryResponse = await fetch(
              `${import.meta.env.VITE_API_URL}/kpi/${sellerId}?from=${month.start}&to=${month.end}`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            if (retryResponse.ok) {
              const data = await retryResponse.json();
              results.push({
                month: month.label,
                value: data[kpiType!] || 0
              });
            } else {
              results.push({ month: month.label, value: 0 });
            }
          }
          
          // Small delay between requests to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`Error fetching data for ${month.label}:`, error);
          results.push({ month: month.label, value: 0 });
        }
      }

      setLoadingProgress(100);
      setHistoricalData(results.filter(d => d.value !== null)); // Filter out months without data

      // If it's paused customers, we still need a special endpoint for the customer list
      if (kpiType === 'paused_customers') {
        fetchPausedCustomersList();
      }
      
      // If it's active customers, fetch the customer list with the latest period
      if (kpiType === 'active_customers' && months.length > 0) {
        // Use the end date of the last month
        const latestPeriodEnd = months[months.length - 1].end;
        fetchActiveCustomersList(latestPeriodEnd);
      }
    } catch (error) {
      console.error('Error fetching historical data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPausedCustomersList = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/kpis/paused-customers/${sellerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPausedCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching paused customers:', error);
    }
  };

  const fetchActiveCustomersList = async (periodEnd?: string) => {
    try {
      // Build URL with optional period_end query parameter
      const url = new URL(`${import.meta.env.VITE_API_URL}/api/v1/kpis/active-customers/${sellerId}`);
      if (periodEnd) {
        url.searchParams.append('period_end', periodEnd);
      }
      
      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setActiveCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching active customers:', error);
    }
  };

  if (!isOpen || !kpiType) return null;

  const kpiConfig = KPI_CONFIG[kpiType as keyof typeof KPI_CONFIG];
  if (!kpiConfig) return null;

  const chartData = {
    labels: historicalData.map(d => d.month),
    datasets: [
      {
        label: kpiConfig.label,
        data: historicalData.map(d => d.value),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.3,
      }
    ]
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0 // Disable animation completely
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `${kpiConfig.label} - Verlauf`,
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            if (kpiType?.includes('cvr') || kpiType === 'termination_rate') {
              return value + '%';
            }
            if (kpiType === 'monthly_revenue_earned') {
              return '€' + value.toLocaleString('de-DE');
            }
            return value;
          }
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <span className="text-2xl">{kpiConfig.icon}</span>
                    {kpiConfig.label} Details
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-64 space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <div className="w-64">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Lade historische Daten... {loadingProgress}%
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${loadingProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Show chart for all KPIs except paused_customers */}
                    {kpiType !== 'paused_customers' && (
                      <div className="h-64 mb-6">
                        <Line data={chartData} options={chartOptions} />
                      </div>
                    )}

                    {kpiType === 'paused_customers' && pausedCustomers.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-md font-semibold mb-3">Pausierte Kunden Liste</h4>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Agentur
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Tage pausiert
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {pausedCustomers.map((customer, index) => (
                                <tr key={customer.contract_id} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700/50' : ''}>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {customer.first_name} {customer.last_name}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {customer.agency_name}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {customer.days_on_pause}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {kpiType === 'active_customers' && activeCustomers.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-md font-semibold mb-3">Aktive Kunden Liste</h4>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Agentur
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Zeitraum
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {activeCustomers.map((customer, index) => (
                                <tr key={customer.cs_id} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700/50' : ''}>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {customer.first_name} {customer.last_name}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {customer.agency_name}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {new Date(customer.bill_start).toLocaleDateString('de-DE')} - {new Date(customer.bill_end).toLocaleDateString('de-DE')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};